package com.example.mystybag.login.views.views

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.foundation.clickable
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.common.extensions.keyboardPadding
import com.example.mystybag.common.ui.components.custom.DefaultCircleShapedTextField
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.BottomSheetUIView
import com.example.mystybag.login.views.components.MerchantRegistrationProgressBar
import com.example.mystybag.login.views.components.MerchantRegistrationProgressFractions
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.rememberUseNativeStyleWebLayout
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p36
import com.example.mystybag.login.logic.AddMolViewModel
import com.example.mystybag.login.model.AddMolRequiredField
import com.example.mystybag.common.views.CountryCodePickerBottomSheet
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp

@Composable
fun AddMolView(viewModel: AddMolViewModel) {
    if (PlatformHelper.isWeb) {
        AddMolWebView(viewModel)
        return
    }
    BottomSheetUIView(
        modifier = Modifier.fillMaxWidth(),
        viewModel = viewModel,
        bottomSheetFactory = {}
    ) { _ ->
        AddMolScreen(viewModel = viewModel, showRegistrationProgress = false)
    }
}

@Composable
internal fun AddMolScreen(
    viewModel: AddMolViewModel,
    showRegistrationProgress: Boolean
) {
    val pinActionsToBottom = rememberUseNativeStyleWebLayout()
    val state by viewModel.uiState.subscribeAsState()
    Box(modifier = Modifier.fillMaxSize()) {
        if (pinActionsToBottom) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .keyboardPadding()
                    .then(
                        if (showRegistrationProgress) Modifier
                        else Modifier.padding(horizontal = p16)
                    )
            ) {
                Column(
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxWidth()
                        .fillMaxHeight()
                        .verticalScroll(rememberScrollState())
                ) {
                    AddMolScrollableInner(
                        viewModel = viewModel,
                        showRegistrationProgress = showRegistrationProgress
                    )
                }
                Spacer(modifier = Modifier.height(p32))
                DefaultButton(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = p16)
                        .padding(bottom = 8.dp),
                    type = DefaultButtonType.PRIMARY,
                    caption = stringResource(SR.strings.add),
                    callback = viewModel::onAdd
                )
            }
        } else {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState())
                    .keyboardPadding()
                    .then(
                        if (showRegistrationProgress) Modifier
                        else Modifier.padding(horizontal = p16)
                    )
            ) {
                AddMolScrollableInner(
                    viewModel = viewModel,
                    showRegistrationProgress = showRegistrationProgress
                )
                Spacer(modifier = Modifier.height(p32))
                DefaultButton(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = p16)
                        .padding(bottom = 8.dp),
                    type = DefaultButtonType.PRIMARY,
                    caption = stringResource(SR.strings.add),
                    callback = viewModel::onAdd
                )
            }
        }

        CountryCodePickerBottomSheet(
            isVisible = state.showCountryPicker,
            countries = state.countries,
            selectedDialCode = state.dialCode,
            onCountrySelected = { country ->
                viewModel.selectCountry(country.id, country.name, country.dialCode)
            },
            onDismiss = viewModel::hideCountryPicker
        )
    }
}

@Composable
private fun AddMolScrollableInner(
    viewModel: AddMolViewModel,
    showRegistrationProgress: Boolean
) {
    val state by viewModel.uiState.subscribeAsState()
    if (showRegistrationProgress) {
        MerchantRegistrationProgressBar(MerchantRegistrationProgressFractions.RESPONSIBLE_PERSONS)
    }
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .then(
                if (showRegistrationProgress) Modifier.padding(horizontal = p16)
                else Modifier
            )
    ) {
        if (showRegistrationProgress) {
            Spacer(modifier = Modifier.height(p36))
        } else {
            Spacer(modifier = Modifier.height(p16))
        }
        Texts.DisplaySmall(
            text = stringResource(SR.strings.add_mol),
            modifier = Modifier.fillMaxWidth()
        )
        Spacer(modifier = Modifier.height(p24))

        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(p24)
        ) {
            DefaultCircleShapedTextField(
                value = state.firstName,
                onValueChange = viewModel::updateFirstName,
                label = stringResource(SR.strings.first_name_label),
                modifier = Modifier.fillMaxWidth(),
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
                isError = AddMolRequiredField.FIRST_NAME in state.requiredFieldErrors,
                errorText = ""
            )
            DefaultCircleShapedTextField(
                value = state.lastName,
                onValueChange = viewModel::updateLastName,
                label = stringResource(SR.strings.last_name_label),
                modifier = Modifier.fillMaxWidth(),
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
                isError = AddMolRequiredField.LAST_NAME in state.requiredFieldErrors,
                errorText = ""
            )
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(p12)
            ) {
                Texts.BodyMedium(
                    text = stringResource(SR.strings.your_mobile_number),
                    modifier = Modifier.fillMaxWidth()
                )
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(p8)
                ) {
                    Box(modifier = Modifier.weight(0.32f)) {
                        DefaultCircleShapedTextField(
                            value = state.dialCode,
                            onValueChange = {},
                            label = stringResource(SR.strings.dial_code),
                            modifier = Modifier.fillMaxWidth(),
                            readOnly = true,
                            enabled = true,
                            canFocus = false,
                            backgroundColor = AppTheme.current.colors.surface,
                            borderColor = AppTheme.current.colors.outline,
                            isError = AddMolRequiredField.DIAL_CODE in state.requiredFieldErrors,
                            errorText = "",
                            trailingIcon = {
                                val err = AddMolRequiredField.DIAL_CODE in state.requiredFieldErrors
                                Image(
                                    painter = painterResource(
                                        when {
                                            state.showCountryPicker -> SRC.images.combobox_arrow_down_green
                                            state.dialCode.isNotEmpty() -> SRC.images.combobox_arrow_right_green
                                            else -> SRC.images.combobox_arrow_right_gray
                                        }
                                    ),
                                    modifier = Modifier.size(p16),
                                    colorFilter = if (err) ColorFilter.tint(AppTheme.current.colors.error) else null
                                )
                            }
                        )
                        Box(
                            modifier = Modifier
                                .matchParentSize()
                                .clickable { viewModel.showCountryPicker() }
                        )
                    }
                    DefaultCircleShapedTextField(
                        value = state.phoneNumber,
                        onValueChange = viewModel::updatePhoneNumber,
                        label = stringResource(SR.strings.phone_number_label),
                        modifier = Modifier.weight(0.68f),
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                        isError = AddMolRequiredField.PHONE_NUMBER in state.requiredFieldErrors,
                        errorText = ""
                    )
                }
            }
        }
    }
}
